import moment from "moment";
import { ModelBase } from "../abstract/model-base.model";
import { AccountEntryExportFormat } from "./account-entry-export-format.model";

// Enums
import { AccountEntryExportProcessingStatus, AccountEntryExportProcessingStatusConfig, ACCOUNT_ENTRY_EXPORT_PROCESSING_STATUSES } from "src/app/@shared/enums/account-entry-export-processing-status.enum";

// Décorateurs
import { OmitField } from "src/app/@shared/decorators/api-field.decorator";

/**
 * Configs de rendu de l'état du job, une seule instance par état pour ne pas créer
 * d'objet à chaque cycle de détection de changement.
 */
const PROCESSING_STATUS_CONFIGS: Record<AccountEntryExportProcessingStatus, AccountEntryExportProcessingStatusConfig> = {
    [AccountEntryExportProcessingStatus.Pending]: { label: 'En attente', severity: undefined, spinning: true },
    [AccountEntryExportProcessingStatus.InProgress]: { label: 'Export en cours', severity: undefined, spinning: true },
    [AccountEntryExportProcessingStatus.Done]: { label: 'Export terminé', severity: 'success' },
    [AccountEntryExportProcessingStatus.Failed]: { label: 'Export échoué', severity: 'danger' },
};

const UNKNOWN_PROCESSING_STATUS_CONFIG: AccountEntryExportProcessingStatusConfig = { label: '', severity: 'secondary' };

/** Entête du bloc d'avertissements écrit par le back dans `logs` (cf. `AccountEntryExport.__log_warnings`). */
const ATTACHMENT_WARNINGS_HEADER = /^\d+ avertissement\(s\) sur les pièces jointes :$/;
const ATTACHMENT_WARNING_LINE = /^\s+- (.+)$/;
const NO_ATTACHMENT_WARNINGS: string[] = [];

export class AccountEntryExport extends ModelBase {

    static override CLASS_NAME: string = 'AccountEntryExport';

    private _attachments!: boolean;
    private _attachmentWarnings!: string[];
    private _compress!: boolean;
    private _dateFrom!: moment.Moment;
    private _dateTo!: moment.Moment;
    private _extracted!: boolean;
    private _format!: AccountEntryExportFormat;
    private _journals!: string[];
    private _logs!: string | null;
    private _processingStatus!: AccountEntryExportProcessingStatus;
    private _processingStatusReason!: string | null;

    public get attachments(): boolean {
        return this._attachments;
    }

    public set attachments(value: any) {
        this._setValue('_attachments', value, 'boolean');
    }

    /**
     * Lignes du bloc « N avertissement(s) sur les pièces jointes » de `logs` : pièces
     * introuvables sautées ou renommées pour éviter une collision. L'archive est
     * produite mais incomplète dès que la liste n'est pas vide.
     */
    @OmitField()
    public get attachmentWarnings(): string[] {
        return this._attachmentWarnings ?? NO_ATTACHMENT_WARNINGS;
    }

    public get compress(): boolean {
        return this._compress;
    }

    public set compress(value: any) {
        this._setValue('_compress', value, 'boolean');
    }

    public get dateFrom(): moment.Moment {
        return this._dateFrom;
    }

    public set dateFrom(value: any) {
        this._setValue('_dateFrom', value, 'date');
    }

    public get dateTo(): moment.Moment {
        return this._dateTo;
    }

    public set dateTo(value: any) {
        this._setValue('_dateTo', value, 'date');
    }

    public get extracted(): boolean {
        return this._extracted;
    }

    public set extracted(value: any) {
        this._setValue('_extracted', value, 'boolean');
    }

    public get format(): AccountEntryExportFormat {
        return this._format;
    }

    public set format(value: any) {
        this._setValue('_format', value, 'AccountEntryExportFormat');
    }

    @OmitField()
    public get hasAttachmentWarnings(): boolean {
        return this.attachmentWarnings.length > 0;
    }

    /**
     * Le job tourne encore (`pending` ou `in_progress`) : la suppression est refusée
     * par le back en HTTP 400, sans que `authorizedActivities` le reflète.
     */
    @OmitField()
    public get isProcessing(): boolean {
        return ACCOUNT_ENTRY_EXPORT_PROCESSING_STATUSES.includes(this.processingStatus);
    }

    @OmitField()
    public get isProcessingFailed(): boolean {
        return this.processingStatus === AccountEntryExportProcessingStatus.Failed;
    }

    public get journals(): string[] {
        return this._journals;
    }

    public set journals(value: any) {
        this._setValue('_journals', value, 'string');
    }

    public get journalsDisp(): string {
        return this.journals.join(', ');
    }

    @OmitField()
    public get logs(): string | null {
        return this._logs;
    }

    public set logs(value: any) {
        this._setValue('_logs', value, 'string');
        this._attachmentWarnings = AccountEntryExport.parseAttachmentWarnings(this._logs);
    }

    @OmitField()
    public get processingStatus(): AccountEntryExportProcessingStatus {
        return this._processingStatus;
    }

    public set processingStatus(value: any) {
        // Le back sérialise l'état en objet { code, label } (ChoiceField).
        // On ne conserve que le code pour rester aligné avec l'enum.
        const code = value && typeof value === 'object' ? value.code : value;
        this._setValue('_processingStatus', code, 'string');
    }

    /**
     * Config visuelle de l'état du job pour rendu badge `<p-tag>`.
     */
    @OmitField()
    public get processingStatusConfig(): AccountEntryExportProcessingStatusConfig {
        return PROCESSING_STATUS_CONFIGS[this.processingStatus] ?? UNKNOWN_PROCESSING_STATUS_CONFIG;
    }

    @OmitField()
    public get processingStatusReason(): string | null {
        return this._processingStatusReason;
    }

    public set processingStatusReason(value: any) {
        this._setValue('_processingStatusReason', value, 'string');
    }

    /**
     * Extrait du journal d'exécution les lignes du bloc d'avertissements sur les
     * pièces jointes. Le bloc commence par « N avertissement(s) sur les pièces
     * jointes : » et se poursuit par une ligne « - … » par pièce.
     */
    public static parseAttachmentWarnings(logs: string | null | undefined): string[] {
        if (!logs) return NO_ATTACHMENT_WARNINGS;
        const lines = logs.split('\n');
        const headerIndex = lines.findIndex(line => ATTACHMENT_WARNINGS_HEADER.test(line.trim()));
        if (headerIndex < 0) return NO_ATTACHMENT_WARNINGS;
        const warnings: string[] = [];
        for (let i = headerIndex + 1; i < lines.length; i++) {
            const match = ATTACHMENT_WARNING_LINE.exec(lines[i]);
            if (!match) break;
            warnings.push(match[1]);
        }
        return warnings.length ? warnings : NO_ATTACHMENT_WARNINGS;
    }

}
